import AutoComplete from '@nile/banyan/src/Pebbles/autocomplete';
import { processData } from '@nile/banyan/src/Utils/autocomplete';
import {
  Tag,
  T2,
  AccessControl,
  DeleteModal,
  StatusTag,
  useIntl,
  getTranslatedText,
  formatFormTemplate
} from '@nile/components';
import {
  Button,
  ButtonGroup,
  ResourceItem,
  ResourceList,
  TextStyle,
  Heading,
  Tippy
} from '@superops/ui-design-system';
import { SVGIcon } from '@superops/super-icons';
import { CustomFormTemplate } from '@nile/mangifera/src/Containers/FormContainers';
import React, { Component, useEffect, useState } from 'react';

class CustomFormCascade extends Component {
  constructor(props) {
    super(props);
    this.state = {
      inputValue: '',
      value: [],
      selectedItems: props.data.length > 0 ? [props.data[0].id] : ['1'],
      fields: props.data ? props.data : []
    };
  }

  static getDerivedStateFromProps(props, state) {
    if (JSON.stringify(props?.data) !== JSON.stringify(state?.fields)) {
      return {
        ...state,
        templates: props.data,
        selectedItems: props.data.length > 0 ? [props.data[0].id] : ['1'],
        selectedTemplate: props.data.length > 0 ? props.data[0] : null
      };
    }
    return null;
  }

  renderSubItem = (item) => {
    this.setState({ selectedField: item });
  };

  componentDidMount() {
    if (this.props.data.length > 0) {
      this.renderSubItem(this.props.data[0]);
    }
  }

  setSelectedItems = (selectedItem) => {
    if (selectedItem.length > 0) {
      this.setState({ selectedItems: [selectedItem[selectedItem.length - 1]] });
    }
  };

  handleFormChange = (selectedItem) => {
    this.setState({ selectedItems: [selectedItem.id] });
    this.renderSubItem(selectedItem);
  };

  deleteCustomForm = (id) => {
    this.props.deleteCustomForm({
      variables: { id }
    });
  };

  render() {
    const intl = useIntl();
    const { selectedField, selectedItems } = this.state;
    const { fields } = this.state;
    const processedAllItems = processData(fields, 'formId');

    return (
      <div className="fh">
        <div className="full-height">
          <div className="fx box-container ch-20">
            <div className="fx no-scroll white-transparent-bg w30 border-right p20 full-height">
              <div className="full-width full-height fx col">
                <div className="sticky-search">
                  <AutoComplete
                    options={processedAllItems}
                    handleChange={this.handleFormChange}
                  />
                </div>
                <div className="cascade-list f11 scroll ch-50">
                  <ResourceList
                    items={fields}
                    selectedItems={selectedItems}
                    onSelectionChange={this.setSelectedItems}
                    renderItem={(item, index) => {
                      const {
                        id,
                        formName,
                        technicians,
                        clients,
                        defaultForm
                      } = item;
                      return (
                        <ResourceItem
                          id={id}
                          accessibilityLabel={`View details for ${formName}`}
                          customClassName="list-no-pad"
                        >
                          <div
                            className="fw list-item"
                            onClick={this.renderSubItem.bind(this, item)}
                          >
                            {!technicians && !clients && (
                              <div className="flex flex-align-center">
                                <TextStyle variation="strong">
                                  {formName}
                                </TextStyle>
                                {defaultForm && (
                                  <StatusTag
                                    className="ml-20 f-12 mr-10"
                                    statusObject={{
                                      color: '#ECECEC',
                                      fontColor: '#000000'
                                    }}
                                  >
                                    <div className="flex center uppercase">
                                      {getTranslatedText(
                                        intl,
                                        'admin.default.tag'
                                      )}
                                    </div>
                                  </StatusTag>
                                )}
                              </div>
                            )}
                          </div>
                        </ResourceItem>
                      );
                    }}
                  />
                </div>
              </div>
            </div>
            {selectedField && (
              <>
                <div className="fx p2040 scroll w70 full-height">
                  <div className="full-width sops-overflow-auto">
                    <div className="flex center h-70">
                      <div className="flex fx2 self-start mt10">
                        <Heading element="h2">{selectedField.formName}</Heading>
                        {selectedField.formStatus === 'DRAFT' && (
                          <Tag className="ml8 height30">
                            <T2>{selectedField.formStatus}</T2>
                          </Tag>
                        )}
                      </div>
                      <div className="fx jend self-start Custom-Fields-Actions">
                        <ButtonGroup>
                          <AccessControl
                            roleToCheck="ticketForm.delete"
                            render={
                              !selectedField?.allowDeleteOrClientVisibilityUpdate ? (
                                <Tippy
                                  title={getTranslatedText(
                                    intl,
                                    'msp.chat.ticketform.delete.tooltip'
                                  )}
                                >
                                  <DeleteModal
                                    description={
                                      'Are you sure you want to delete this form?'
                                    }
                                    handleDeleteConfirmation={() => {
                                      this.deleteCustomForm(selectedField.id);
                                    }}
                                    title="Delete custom form"
                                    disableActivator={
                                      !selectedField?.allowDeleteOrClientVisibilityUpdate
                                    }
                                  />
                                </Tippy>
                              ) : (
                                <DeleteModal
                                  description={
                                    'Are you sure you want to delete this form?'
                                  }
                                  handleDeleteConfirmation={() => {
                                    this.deleteCustomForm(selectedField.id);
                                  }}
                                  title="Delete custom form"
                                  disableActivator={
                                    !selectedField?.allowDeleteOrClientVisibilityUpdate
                                  }
                                />
                              )
                            }
                          />
                          <AccessControl
                            roleToCheck="ticketForm.edit"
                            render={
                              <Button
                                secondary
                                icon={<SVGIcon name="Edit" />}
                                url={
                                  '#/onboard/formbuilder/' +
                                  selectedField.id +
                                  '/edit'
                                }
                              />
                            }
                          />
                        </ButtonGroup>
                      </div>
                    </div>
                    <FormBuilder selectedFormTemplate={selectedField} />
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }
}

function FormBuilder(props) {
  const { selectedFormTemplate } = props;

  const [formattedFormTemplate, setFormattedFormTemplate] = useState(null);

  useEffect(() => {
    const formattedFieldDetails = formatFormTemplate(
      selectedFormTemplate?.fieldDetails,
      true
    );

    const updatedFormTemplate = {
      ...selectedFormTemplate,
      fieldDetails: formattedFieldDetails
    };

    setFormattedFormTemplate(updatedFormTemplate);
  }, [selectedFormTemplate]);

  const [mandatoryFieldsOnCreation, setMandatoryFieldsOnCreation] = useState(
    []
  );
  return (
    formattedFormTemplate && (
      <CustomFormTemplate
        disableSelection
        selectedFormTemplate={formattedFormTemplate}
        mandatoryFieldsOnCreation={mandatoryFieldsOnCreation}
        setMandatoryFieldsOnCreation={setMandatoryFieldsOnCreation}
      />
    )
  );
}

export default CustomFormCascade;
